import { takesMoney } from "../utils/takesMoney";
import { masspayVerified, openMoneyGate, whichGate } from "../lib/connectBank";
import { openMasspayWallet } from "../lib/masspayWallet";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { ordersApi } from "../services/orders";
import { paymentsApi } from "../services/payments";
import { setlistsApi } from "../services/setlists";
import { stripeApi } from "../services/stripe";
import { usersApi } from "../services/users";
import type { InstantPayoutStatus } from "../types/payments";
import type { User } from "../types/User";
import { formatSingleAmount, formatTxnAmount } from "../utils/currency";
import { minCashoutFor } from "../utils/payinConfig";
import { minCashoutLock, minCashoutInfoFrom, type MinCashoutLock } from "../utils/minCashoutLock";
import { openExternal } from "../lib/openExternal";
import "./BalancePage.css";
import { pageCache } from "../lib/pageCache";
import { useAccountCache } from "../hooks/useAccountCache";
import SetlistUploadFlow from "../components/SetlistUploadFlow";
import SetlistSealedArt from "../components/SetlistSealedArt";
import AddDebitCardSheet, { canAddCardInApp } from "../components/AddDebitCardSheet";
import InstantPayoutsPopup from "../components/InstantPayoutsPopup";
import InstantIntroSheet from "../components/InstantIntroSheet";
import { useInstantIntro } from "../hooks/useInstantIntro";
import BalanceBankChip from "../components/BalanceBankChip";
import { PayoutOnItsWay, PayoutTrackerSheet, payoutAmount } from "../components/PayoutTracker";
import type { PayoutBatch } from "../types/payouts";
import { movingPayout, trackerShortDate } from "../utils/payoutTracker";
import { t as tr, formatLocale } from "../i18n";
import { T } from "../i18n/T";
import { ReferralBonusRow } from "../components/ReferralCashout";
import CashoutFlow from "../components/CashoutFlow";
import { cashoutDataFrom, type CashoutData } from "../utils/cashoutData";

// Instant Payouts — GET /payments/instant-payouts. Shown ONLY
// when the talent clears the rules (founder: only show it to the eligible):
// needsCard → the "Add card" row under the card; set up → a gold "⚡ INSTANT"
// half on the card's bank chip (founder pick B2, build 44).

// Join per-currency amounts into one display string (never blended, never a
// hardcoded "$"). Empty → the talent's own-currency zero.
function joinMoney(
  byCur: Record<string, number>,
  fallbackCurrency?: string
): string {
  const parts = Object.entries(byCur)
    .filter(([, amt]) => amt > 0)
    .map(([cur, amt]) => formatSingleAmount(amt, cur));
  return parts.length > 0
    ? parts.join(" • ")
    : formatSingleAmount(0, fallbackCurrency);
}

// The card's number = requests + Refer & Earn money added to the balance
// (GET /orders/earnings referralByCurrency) — both cash out together.
function plusReferral(byCur: Record<string, number>, referral: Record<string, number>): Record<string, number> {
  const out = { ...byCur };
  for (const [cur, amt] of Object.entries(referral)) out[cur] = (out[cur] || 0) + amt;
  return out;
}

// The QR card's wave lines (founder pick B) — the same geometry as
// walletPassRenderer.drawWaves, precomputed once for a 1.586:1 box (the
// balance card's credit-card ratio): 7 sine lines across the left 45%,
// rows from 55% of the height in 4% steps, 2.5% amplitude.
const WAVE_W = 1586;
const WAVE_H = 1000;
const WAVE_PATHS: string[] = Array.from({ length: 7 }, (_, i) => {
  const y0 = WAVE_H * 0.55 + i * (WAVE_H * 0.04);
  const parts = [`M-20,${y0.toFixed(1)}`];
  for (let x = 0; x <= WAVE_W * 0.45; x += 12) {
    const phase = (x + i * 60) / (WAVE_W * 0.08);
    const y = y0 - Math.sin(phase) * (WAVE_H * 0.025);
    parts.push(`L${x},${y.toFixed(1)}`);
  }
  return parts.join(" ");
});

const BalancePage: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // Seed every data group from the session cache so tab revisits render the
  // last-known numbers instantly (no loading flash); the fetches below still
  // run every mount and overwrite silently. Keys are per-slug.
  const ck = useCallback((part: string) => `balance-${part}:${user?.slug || "anon"}`, [user?.slug]);
  const cProfile = pageCache.get<any>(ck("profile"));
  const cBalance = pageCache.get<Record<string, number>>(ck("byCur"));
  const cBank = pageCache.get<{ last4: string | null; brand: string | null }>(ck("bank"));
  const cPayouts = pageCache.get<{ count: number; lastDate: string | null; moving?: PayoutBatch | null }>(ck("payouts"));

  const [displayName, setDisplayName] = useState(cProfile?.displayName ?? "");
  const [ownCurrency, setOwnCurrency] = useState<string>(cProfile?.ownCurrency ?? "usd");
  // Fraud door: Stripe's payout delay for this account, mirrored
  // by the server (User.payoutHoldDays). New accounts start at 7; an admin
  // lift drops it to the country minimum. Drives the one-line hold notice
  // under Withdraw (founder pick A) — shown only while the hold is ≥ 7 days.
  const [payoutHoldDays, setPayoutHoldDays] = useState<number | null>(cProfile?.payoutHoldDays ?? null);
  // Our hold was lifted: any hold left is Stripe's own minimum, which isn't
  // "first payouts" — the note under Withdraw stays away (2026-10-22).
  const [holdLifted, setHoldLifted] = useState<boolean>(!!cProfile?.payoutHoldLiftedAt);
  const [balanceByCur, setBalanceByCur] = useState<Record<string, number>>(cBalance ?? {});
  const [balanceLoaded, setBalanceLoaded] = useState(!!cBalance);
  // Refer & Earn money added to the balance, not cashed out yet. Kept apart
  // from balanceByCur so it never fires the "+X" overnight chip (founder
  // 10/22: a "Referral bonus" line, no chip on the card).
  const [referralByCur, setReferralByCur] = useState<Record<string, number>>(
    pageCache.get<Record<string, number>>(ck("ref")) ?? {}
  );

  const [bank, setBank] = useState<{ last4: string | null; brand: string | null }>(
    cBank ?? { last4: null, brand: null }
  );

  const [lastPayoutDate, setLastPayoutDate] = useState<string | null>(cPayouts?.lastDate ?? null);
  // Count of payout batches (endpoint returns newest-first, capped at 100).
  const [payoutCount, setPayoutCount] = useState(cPayouts?.count ?? 0);
  // Payout tracker (1.5.2): the newest cash-out still on its way, and its sheet.
  const [moving, setMoving] = useState<PayoutBatch | null>(cPayouts?.moving ?? null);
  const [trackerOpen, setTrackerOpen] = useState(false);

  // Free tier: the talent's own profile, for the one money-gate question.
  // gateProfile is THIS visit's server copy (null until it lands) — the only
  // one a money tap reads. gateShown is the last one seen, for painting (the
  // MassPay chip, Withdraw's look) the moment the tab opens (1.5.2).
  const [gateProfile, setGateProfile] = useState<Partial<User> | null>(null);
  const [gateShown, setGateShown] = useAccountCache<Partial<User> | null>("bal-gate", null);
  const gateLook = gateProfile ?? gateShown;
  const [bankLinkBusy, setBankLinkBusy] = useState(false);
  // Instant payouts: painted from the last visit (the gold chip, the "Add
  // card" row); a tap waits for this visit's answer (openInstantPopup).
  const [instant, setInstant] = useAccountCache<InstantPayoutStatus | null>("bal-instant", null);
  const instantFresh = useRef(false);
  // THIS visit's answer only (null until it lands) — what the one-time
  // "Instant payouts are here" sheet decides from, never the painted copy.
  const [freshInstant, setFreshInstant] = useState<InstantPayoutStatus | null>(null);
  // Under the minimum nothing can be withdrawn, instant included — the row
  // then says "Min. $5" instead of "✓ Ready". Same rule as the Transfer modal.
  // undefined = earnings not back yet → the row's right side stays empty
  // rather than flashing the wrong answer. Fresh only (taps read it);
  // lockShown is the last one, for Withdraw's grey look at first paint.
  const [cashoutLock, setCashoutLock] = useState<MinCashoutLock | null | undefined>(undefined);
  const [lockShown, setLockShown] = useAccountCache<MinCashoutLock | null | undefined>("bal-lock", undefined);
  // The Instant Payouts row's explainer popup (founder, 1.5.1 #8).
  const [instantPopupOpen, setInstantPopupOpen] = useState(false);
  const [withdrawTipOpen, setWithdrawTipOpen] = useState(false);
  // Any tap elsewhere closes the Withdraw tip (iOS never blurs a tapped
  // button). Withdraw's own click stops at the button, so tapping it again
  // just toggles.
  useEffect(() => {
    if (!withdrawTipOpen) return;
    const close = () => setWithdrawTipOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [withdrawTipOpen]);
  const [cardSheetOpen, setCardSheetOpen] = useState(false);

  // Setlist sales waiting on the track-history upload (accepted, uncaptured —
  // NOT in the balance number above; delivery is what captures the money).
  // Painted from the last visit; cancelling reads this visit's list first.
  const [setlistPending, setSetlistPending] = useAccountCache<{
    count: number;
    total: number;
    currency: string | null;
    oldestAcceptedAt: string | null;
    // The waiting sales' ids — what "Cancel the sale" declines.
    // Empty on an older server, which simply hides the cancel line.
    orderIds: string[];
  }>("bal-setlist", { count: 0, total: 0, currency: null, oldestAcceptedAt: null, orderIds: [] });
  const [setlistFresh, setSetlistFresh] = useState(false);
  // "Not delivering this set?" — cancels every waiting sale through the
  // existing decline path (the fan's uncaptured hold is released, they get
  // the never-charged email). Confirm sheet first: this is money-adjacent
  // and can't be undone.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelErr, setCancelErr] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Bumped after a delivery so the balance number refetches (the captures
  // just landed in it).
  const [balanceRefresh, setBalanceRefresh] = useState(0);
  // Withdraw opens the Transfer-to-Bank flow right here, over Balance (founder,
  // 1.5.2 b49: it used to jump to the Requests page). Read from the same
  // earnings answer as the balance number (utils/cashoutData).
  const [cashout, setCashout] = useState<CashoutData | null>(null);
  // The server's withdrawal line per currency for THIS talent ($15 for US-tier
  // talent since 2026-10-29) — the zero-balance Instant card reads it too.
  const [minLineByCur, setMinLineByCur] = useState<Record<string, number>>({});
  // Delivery count-up (founder decision): when a delivery just landed, the new
  // total rolls up from the old one and the card pulses. The rolling number
  // is display-only — the real total snaps in at the end, formatted by the
  // same joinMoney as always, so what the DJ reads is never a tween value.
  const bumpArmedRef = useRef(false);
  const prevByCurRef = useRef<Record<string, number> | null>(null);
  // The last balance this DJ saw on THIS device (founder: a laptop
  // upload, back on the phone an hour later, should still get the roll-up).
  // Any growth since they last looked rolls up on the next visit; a drop
  // (a payout) just shows. Storage can be missing or throw — then nothing
  // animates and nothing breaks.
  const seenId = user?.id || user?._id;
  const seenKey = seenId ? `requestapp.balanceSeen.${seenId}` : null;
  const readSeen = useCallback((): Record<string, number> | null => {
    if (!seenKey) return null;
    try {
      const raw = window.localStorage.getItem(seenKey);
      const v = raw ? JSON.parse(raw) : null;
      return v && typeof v === "object" && !Array.isArray(v) ? v : null;
    } catch {
      return null;
    }
  }, [seenKey]);
  const writeSeen = useCallback((v: Record<string, number>) => {
    if (!seenKey) return;
    try {
      window.localStorage.setItem(seenKey, JSON.stringify(v));
    } catch {
      /* private mode / quota — the roll-up is a nicety */
    }
  }, [seenKey]);
  const [rollingByCur, setRollingByCur] = useState<Record<string, number> | null>(null);
  const [bumpDelta, setBumpDelta] = useState<{ amount: number; currency: string } | null>(null);

  const fetchSetlistPending = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const d = (await setlistsApi.pending()) || ({} as any);
      const next = {
        count: Number(d.count) || 0,
        total: Number(d.total) || 0,
        currency: d.currency || null,
        oldestAcceptedAt: d.oldestAcceptedAt || null,
        orderIds: Array.isArray(d.orderIds) ? d.orderIds.map(String) : [],
      };
      setSetlistPending(next);
      setSetlistFresh(true);
      return next;
    } catch {
      /* older server / transient — the card just stays hidden */
      return null;
    }
  }, [isAuthenticated, setSetlistPending]);

  useEffect(() => {
    fetchSetlistPending();
  }, [fetchSetlistPending]);

  const cancelSales = async () => {
    if (cancelBusy || setlistPending.orderIds.length === 0) return;
    setCancelBusy(true);
    setCancelErr(null);
    try {
      // Only the sales the server lists THIS visit (never the remembered list).
      const orderIds = setlistFresh ? setlistPending.orderIds : (await fetchSetlistPending())?.orderIds || [];
      if (orderIds.length === 0) {
        setCancelOpen(false);
        return;
      }
      const data = await ordersApi.batchDecline({ orderIds, reason: "not_sharing_setlist" });
      // Partial success is a 200 with a `failed` list (a hold Stripe wouldn't
      // release). Refetch so the card shows what's still waiting, and keep the
      // sheet up with the truth — never a silent "done" over a sale that isn't.
      fetchSetlistPending();
      const failed = Array.isArray(data?.failed) ? data.failed.length : 0;
      if (failed > 0) {
        setCancelErr(
          tr("balancePage.couldntCancelSales", { count: failed })
        );
        return;
      }
      setCancelOpen(false);
      setUploadOpen(false);
    } catch {
      // The server's own wording here is Stripe-speak ("All refunds failed");
      // the DJ needs the plain truth: nothing changed, the fan still holds.
      setCancelErr(tr("balancePage.couldnTCancelThe"));
    } finally {
      setCancelBusy(false);
    }
  };

  // /balance?upload=1 — the accept email's laptop link (and the flow's own
  // "copy this link" button) opens the upload flow the moment the page knows
  // sales are waiting. Fires once per visit to the link; nothing waiting → the
  // plain Balance page (amber card absent) already tells the story.
  const [searchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    // Re-armed once the link is gone from the URL: the Transfer sheet's
    // playlist-sales line (CashoutFlow) links here again from Balance itself.
    if (searchParams.get("upload") !== "1") {
      autoOpenedRef.current = false;
      return;
    }
    if (autoOpenedRef.current) return;
    if (!setlistFresh || setlistPending.count <= 0) return;
    autoOpenedRef.current = true;
    setUploadOpen(true);
  }, [searchParams, setlistPending.count, setlistFresh]);


  // Profile (name, currency, lifetime gross)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    usersApi.get(user.slug).then((res) => {
      if (cancelled) return;
      const data: Partial<User> = res || {};
      const holdDays = typeof data.payoutHoldDays === "number" ? data.payoutHoldDays : null;
      pageCache.set(ck("profile"), {
        displayName: data.name || "",
        ownCurrency: (data.currency || "usd").toLowerCase(),
        earnedByCurrency: data.earnedByCurrency || {},
        earnedAmount: Number(data.earnedAmount) || 0,
        payoutHoldDays: holdDays,
        payoutHoldLiftedAt: data.payoutHoldLiftedAt ?? null,
      });
      setDisplayName(data.name || "");
      setOwnCurrency((data.currency || "usd").toLowerCase());
      setPayoutHoldDays(holdDays);
      setHoldLifted(!!data.payoutHoldLiftedAt);
      setGateProfile(data);
      setGateShown(data);
    }).catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [user, isAuthenticated, ck, setGateShown]);

  // A money tap that beat this visit's profile reads it now (never the
  // remembered one). null = couldn't read it: the tap does nothing.
  const freshGate = useCallback(async (): Promise<Partial<User> | null> => {
    if (gateProfile) return gateProfile;
    if (!user) return null;
    try {
      const data: Partial<User> = (await usersApi.get(user.slug)) || {};
      setGateProfile(data);
      setGateShown(data);
      return data;
    } catch {
      return null;
    }
  }, [gateProfile, user, setGateShown]);

  // Current balance (played-unsettled). Same endpoint the Requests page and
  // header use; per-currency totals are never blended.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let retry = 0;
    ordersApi.earnings().then((res) => {
      if (cancelled) return;
      const payload = res || {};
      setCashout(cashoutDataFrom(payload, user?.currency));
      const byCur: Record<string, number> = {};
      if (payload.byCurrency && typeof payload.byCurrency === "object" && !Array.isArray(payload.byCurrency)) {
        for (const [k, v] of Object.entries<any>(payload.byCurrency)) {
          const cur = String(k || "").trim().toLowerCase() || "usd";
          const n = Number(v?.total);
          byCur[cur] = (byCur[cur] || 0) + (Number.isFinite(n) ? n : 0);
        }
      } else if (typeof payload.total === "number") {
        byCur[user?.currency || "usd"] = payload.total;
      }
      pageCache.set(ck("byCur"), byCur);
      const refByCur: Record<string, number> = {};
      if (payload.referralByCurrency && typeof payload.referralByCurrency === "object") {
        for (const [k, v] of Object.entries(payload.referralByCurrency)) {
          const n = Number(v);
          if (Number.isFinite(n) && n > 0) refByCur[String(k || "usd").toLowerCase()] = n;
        }
      }
      pageCache.set(ck("ref"), refByCur);
      setReferralByCur(refByCur);
      // Grew since the DJ last looked on this device → roll up from that
      // number. (A delivery in this session already armed the bump with the
      // live previous total, so leave that alone.)
      if (!bumpArmedRef.current) {
        const seen = readSeen();
        if (seen && Object.entries(byCur).some(([cur, amt]) => amt - (seen[cur] || 0) > 0.005)) {
          prevByCurRef.current = seen;
          bumpArmedRef.current = true;
        }
      }
      writeSeen(byCur);
      setBalanceByCur(byCur);
      setBalanceLoaded(true);
      // The per-currency minimum line (main #560) and the lock, which counts
      // Refer & Earn money too (1.5.2).
      const minInfo = minCashoutInfoFrom(payload);
      setMinLineByCur(minInfo.byCurrency);
      const lock = minCashoutLock({
        earningsByCur: Object.fromEntries(
          Array.from(new Set([...Object.keys(byCur), ...Object.keys(refByCur)])).map((cur) => [cur, { total: byCur[cur] || 0, referral: refByCur[cur] || 0 }])
        ),
        minCashoutInfo: minInfo,
      });
      setCashoutLock(lock);
      setLockShown(lock);
    }).catch(() => {
      // A failed read is not a zero balance (2026-10-10): it used to show
      // $0.00 until the page was opened again. Keep the last good number (or
      // "—" when there is none) and quietly try again.
      if (!cancelled) retry = window.setTimeout(() => setBalanceRefresh((k) => k + 1), 10_000);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(retry);
    };
  }, [isAuthenticated, user?.currency, balanceRefresh, ck, readSeen, writeSeen, setLockShown]);

  useEffect(() => {
    const prev = prevByCurRef.current;
    prevByCurRef.current = balanceByCur;
    if (!bumpArmedRef.current || !prev) return;
    bumpArmedRef.current = false;
    // Biggest gain decides the "+X" chip (one currency per night in practice).
    let best: { amount: number; currency: string } | null = null;
    for (const [cur, amt] of Object.entries(balanceByCur)) {
      const d = amt - (prev[cur] || 0);
      if (d > 0.005 && (!best || d > best.amount)) best = { amount: d, currency: cur };
    }
    if (!best) return;
    setBumpDelta(best);
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const DUR = reduce ? 0 : 1400;
    const start = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const t = DUR === 0 ? 1 : Math.min(1, (now - start) / DUR);
      const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
      if (t < 1) {
        const mid: Record<string, number> = {};
        for (const [cur, amt] of Object.entries(balanceByCur)) {
          const from = prev[cur] || 0;
          mid[cur] = from + (amt - from) * e;
        }
        setRollingByCur(mid);
        raf = requestAnimationFrame(frame);
      } else {
        setRollingByCur(null);
      }
    };
    raf = requestAnimationFrame(frame);
    const chip = window.setTimeout(() => setBumpDelta(null), 2000);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(chip);
    };
  }, [balanceByCur]);

  // Bank last-4 for the on-card chip → Stripe dashboard.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    usersApi.bank(user.slug)
      .then((res) => {
        if (cancelled) return;
        const next = { last4: res?.bankLast4 || null, brand: res?.bankBrand || null };
        pageCache.set(ck("bank"), next);
        setBank(next);
      })
      .catch(() => { /* not connected yet — chip just hides */ });
    return () => { cancelled = true; };
  }, [user, isAuthenticated, ck]);

  // Instant-payout status → the "⚡ Instant payouts" row. Re-checked when the
  // app comes back to the foreground (the talent returns from adding a card
  // in Stripe), so "Add card" flips to "✓ Ready" without a reload.
  const loadInstant = useCallback(async () => {
    const res = await paymentsApi.instantPayoutStatus();
    instantFresh.current = true;
    setInstant(res);
    setFreshInstant(res);
    return res;
  }, [setInstant]);
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    const load = () => {
      paymentsApi
        .instantPayoutStatus()
        .then((res) => {
          if (cancelled) return;
          instantFresh.current = true;
          setInstant(res);
          setFreshInstant(res);
        })
        .catch(() => {
          /* older server / not connected yet — the row just hides */
        });
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, isAuthenticated, setInstant]);

  // The row and the chip only explain — but what they explain (and "Add
  // debit card" behind it) is this visit's answer: a tap before it landed
  // reads it now, and nothing opens when the row no longer applies.
  const openInstantPopup = async () => {
    if (!instantFresh.current) {
      const res = await loadInstant().catch(() => null);
      if (!res?.rulesPass) return;
    }
    setInstantPopupOpen(true);
  };

  // "Instant payouts are here" — once per account, decided from this visit's
  // answers only (utils/instantIntro). Waits while any other sheet is up.
  const [introOpen, closeIntro] = useInstantIntro(
    seenId,
    freshInstant,
    gateProfile,
    instantPopupOpen || cardSheetOpen || uploadOpen || cancelOpen || trackerOpen || searchParams.get("upload") === "1"
  );

  // Recent-payout date for the "Recent Payouts" row sub-label.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    usersApi.payouts(user.slug).then((res) => {
      if (cancelled) return;
      const rows = Array.isArray(res) ? res : [];
      const lastDate = rows[0]?.createdAt
        ? new Date(rows[0].createdAt).toLocaleDateString(formatLocale(), {
            month: "short", day: "numeric", year: "numeric",
          })
        : null;
      const nowMoving = movingPayout(rows);
      pageCache.set(ck("payouts"), { count: rows.length, lastDate, moving: nowMoving });
      setPayoutCount(rows.length);
      setMoving(nowMoving);
      if (lastDate) setLastPayoutDate(lastDate);
    }).catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [user, isAuthenticated, ck, balanceRefresh]);

  const balanceParts = useMemo(
    () => Object.entries(plusReferral(balanceByCur, referralByCur)).filter(([, amt]) => amt > 0),
    [balanceByCur, referralByCur]
  );
  const hasBalance = balanceParts.length > 0;
  // What the Instant popup says ("Almost there") while nothing can be
  // withdrawn: the minimum, in the currency the money would leave in.
  const instantCur = instant?.payoutCurrency || ownCurrency;
  const instantFrom =
    cashoutLock === undefined
      ? undefined
      : cashoutLock ?? (hasBalance ? null : { currency: instantCur, minimum: Number(minLineByCur[instantCur]) || minCashoutFor(instantCur) });
  // Money on the card, but under the minimum (and not past the 30-day waiver).
  // What a tap does reads this visit's lock; the grey look may come from the
  // last visit's until it lands (undefined = not back yet).
  const withdrawLocked = hasBalance && !!cashoutLock;
  const withdrawLooksLocked = hasBalance && !!(cashoutLock === undefined ? lockShown : cashoutLock);
  const instantToCard = instant?.destination?.object === "card";
  // Stripe's bank names carry the legal suffix ("BANK OF AMERICA, N.A.") —
  // dropped: it says nothing to the talent and costs the line its room.
  const instantDestName =
    (instant?.destination?.brand || "").replace(/,?\s+N\.?\s?A\.?$/i, "") || (instantToCard ? tr("balancePage.debitCard") : tr("balancePage.bank"));
  // "loading" = earnings not back yet → no popup rather than a wrong one.
  const instantState = !instant?.eligible
    ? "needsCard"
    : instantFrom === undefined
      ? "loading"
      : instantFrom
        ? "locked"
        : "ready";
  // Set up: the rules pass and Stripe has somewhere to pay instantly.
  const instantOn = !!instant?.rulesPass && !!instant?.eligible;
  const balanceDisplay = balanceLoaded ? joinMoney(plusReferral(rollingByCur ?? balanceByCur, referralByCur), ownCurrency) : "—";

  const openStripe = useCallback(async () => {
    if (bankLinkBusy) return;
    setBankLinkBusy(true);
    try {
      const res = await stripeApi.dashboardLink();
      const url = res?.url;
      if (url) openExternal(url);
      else alert(tr("balancePage.couldnTOpenStripe"));
    } catch (err: any) {
      alert(
        err?.response?.data?.msg ||
          tr("balancePage.couldnTOpenStripe2")
      );
    } finally {
      setBankLinkBusy(false);
    }
  }, [bankLinkBusy]);

  // Recent payouts → full history page.
  const payoutsRow = (
    <div className="balance-row">
      <div>
        <div className="balance-row-label">
          {payoutCount > 0 && (
            <span className="balance-payout-count">
              {payoutCount >= 100 ? "100+" : payoutCount}{" "}
            </span>
          )}
          {payoutCount === 1 ? tr("balancePage.recentPayout") : tr("balancePage.recentPayouts")}
        </div>
        {lastPayoutDate && !moving && (
          <div className="balance-row-sub">{tr("balancePage.last", { lastPayoutDate })}</div>
        )}
        {/* Short phone with a playlist sale waiting: no room for the strip,
            so the cash-out takes the "Last:" line instead (PayoutTracker.css). */}
        {moving && (
          <button type="button" className="ptk-inline" onClick={() => setTrackerOpen(true)}>
            {moving.arrivalAt
              ? tr("payoutTracker.inlineArrivesBy", { amount: payoutAmount(moving), date: trackerShortDate(moving.arrivalAt) })
              : tr("payoutTracker.onItsWay", { amount: payoutAmount(moving) })}
          </button>
        )}
      </div>
      <button
        type="button"
        className="balance-row-link"
        // No bank yet (founder, build 44): nothing to show — open Connect Bank.
        onClick={async () => {
          const gate = await freshGate();
          if (gate && !takesMoney(gate) && whichGate(gate) === "bank") openMoneyGate(gate);
          else navigate("/payout-history");
        }}
      >
        {tr("balancePage.viewAll")}
      </button>
    </div>
  );

  // The Transfer-to-Bank flow over Balance (Withdraw), placed at the bottom of the page.
  const cashoutFlow = cashout ? (
    <CashoutFlow
      earningsByCur={cashout.earningsByCur}
      setEarningsByCur={(next) =>
        setCashout((c) => (c ? { ...c, earningsByCur: typeof next === "function" ? next(c.earningsByCur) : next } : c))
      }
      commissionRate={cashout.commissionRate}
      refundFeesByCur={cashout.refundFeesByCur}
      setlistPending={setlistPending}
      minCashoutInfo={cashout.minCashoutInfo}
      masspayHold={cashout.masspayHold}
      profile={(gateProfile as User | null) ?? user}
      // After a cash-out: the balance number and Recent Payouts read again.
      fetchAll={async () => setBalanceRefresh((k) => k + 1)}
    />
  ) : null;

  return (
    <div className="balance-page">
      <h2 className="balance-title">{tr("balancePage.balance")}</h2>

      {/* M3 debit card — brand-green, wordmark on it, bank chip → Stripe */}
      {/* Credit-card proportions (ISO ID-1, 1.586:1 — founder:
          "what almost all fintech apps have") with the R watermark on the
          right. The amount steps its size down by string length so a long
          multi-currency balance never runs into the R or off the card. */}
      <div className={bumpDelta ? "balance-card balance-card--bump" : "balance-card"}>
        <span
          className="balance-card-r"
          aria-hidden="true"
          style={{ WebkitMaskImage: "url(/r.png)", maskImage: "url(/r.png)" }}
        />
        <svg
          className="balance-card-waves"
          viewBox={`0 0 ${WAVE_W} ${WAVE_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <g fill="none" stroke="#1DA550" strokeOpacity={0.4} strokeWidth={1} vectorEffect="non-scaling-stroke" strokeLinecap="round">
            {WAVE_PATHS.map((d, i) => (
              <path key={i} d={d} vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        </svg>
        <img src="/request-wordmark.png" alt={tr("balancePage.requestApp")} className="balance-card-logo" />
        <div className="balance-card-amount-block">
          <div className="balance-card-label">{tr("balancePage.currentBalance")}</div>
          <div
            className="balance-card-amount"
            data-len={balanceDisplay.length > 14 ? "xl" : balanceDisplay.length > 10 ? "l" : "s"}
          >
            {balanceDisplay}
          </div>
          {bumpDelta && (
            <span className="balance-card-delta" aria-live="polite">
              +{formatTxnAmount(bumpDelta.amount, bumpDelta.currency)}
            </span>
          )}
        </div>
        <div className="balance-card-foot">
          <span className="balance-card-name">{displayName}</span>
          <BalanceBankChip
            last4={bank.last4}
            brand={bank.brand}
            instant={instantOn}
            busy={bankLinkBusy}
            masspay={masspayVerified(gateLook)}
            onOpenMasspay={() => void openMasspayWallet()}
            onOpenStripe={openStripe}
            onOpenInstant={() => {
              // "loading" = earnings not back yet: no popup rather than a wrong one.
              if (instantState !== "loading") void openInstantPopup();
            }}
          />
        </div>
      </div>

      {/* Instant payouts not set up yet — only for talent who clear the rules
          (founder mockup, build 38). Once set up the row goes and the bank
          chip on the card turns gold instead (founder pick B2, build 44).
          Every tap only EXPLAINS — a popup, never a cash-out (founder, 1.5.1
          #8: the green Withdraw button is the one way money leaves):
            the row            → "Add debit card" → the card sheet / Stripe
            the chip, ≥ minimum → where the money lands
            the chip, < minimum → where instant payouts start ("Min. $5")
          Everyone else never sees either. */}
      {instant?.rulesPass && instantState === "needsCard" && (
        <button
          type="button"
          className="instant-card"
          aria-haspopup="dialog"
          disabled={bankLinkBusy}
          onClick={() => void openInstantPopup()}
        >
          <span className="instant-card-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#24C961" strokeWidth="2" strokeLinejoin="round">
              <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12L13 2Z" />
            </svg>
          </span>
          <span className="instant-card-main">
            <span className="instant-card-title">{tr("balancePage.instantPayouts")}</span>
            <span className="instant-card-sub">{tr("balancePage.minutesToDebitCard")}</span>
          </span>
          <span className="instant-card-pill">{tr("balancePage.addCard")}</span>
          <svg className="instant-card-chevron" width="9" height="14" viewBox="0 0 9 14" fill="none" stroke="#24C961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="m1.5 1.5 5.5 5.5-5.5 5.5" />
          </svg>
        </button>
      )}
      {instantPopupOpen && instantState !== "loading" && (
        <InstantPayoutsPopup
          state={instantState}
          destination={instant?.destination?.last4 ? `${instantDestName} •••${instant.destination.last4}` : instantDestName}
          minimum={instantFrom ? formatSingleAmount(instantFrom.minimum, instantFrom.currency, 0) : ""}
          last4={instant?.destination?.last4}
          toBank={instant?.destination?.object === "bank_account"}
          onAddCard={() => {
            // Exactly what tapping the row did before 1.5.1.
            setInstantPopupOpen(false);
            if (canAddCardInApp) setCardSheetOpen(true);
            else openStripe();
          }}
          onClose={() => setInstantPopupOpen(false)}
        />
      )}
      {introOpen && (
        <InstantIntroSheet
          onAddCard={() => {
            // Exactly what the "Add card" row's popup does.
            closeIntro();
            if (canAddCardInApp) setCardSheetOpen(true);
            else openStripe();
          }}
          onClose={closeIntro}
        />
      )}
      {cardSheetOpen && (
        <AddDebitCardSheet
          currency={instant?.payoutCurrency || ownCurrency}
          onClose={() => setCardSheetOpen(false)}
          onAdded={(_dest, refreshed) => {
            setCardSheetOpen(false);
            if (refreshed) setInstant(refreshed);
          }}
        />
      )}

      {/* Playlist sales waiting on the track-history upload (v4 DJ 06) —
          visible, not withdrawable: one upload collects every waiting sale. */}
      {/* Playlist sales waiting for the track history (founder pick A —
          reads like the sale email): the sealed record, then the email's two
          lines, then a pill that hugs its text. Free sales get the email's
          other wording. Cancelling lives in the upload screen now. */}
      {setlistPending.count > 0 && (
        <div className="balance-setlist-card">
          <div className="balance-setlist-art" aria-hidden="true">
            <SetlistSealedArt compact withMarks />
          </div>
          <div className="balance-setlist-title">
            {setlistPending.total > 0 ? (
              <>
                <T k="balancePage.peoplePurchasedSetlist" vars={{ count: setlistPending.count, amount: formatTxnAmount(setlistPending.total, setlistPending.currency || ownCurrency) }} tags={{ 0: (c) => <b>{c}</b>, 1: (c) => <b>{c}</b>, 2: (c) => <b>{c}</b> }} />
              </>
            ) : (
              <>
                <T k="balancePage.peopleAskedSetlist" vars={{ count: setlistPending.count }} tags={{ 0: (c) => <b>{c}</b> }} />
              </>
            )}
          </div>
          <div className="balance-setlist-sub">
            {setlistPending.total > 0 ? tr("balancePage.toReceiveFundsAnd") : tr("balancePage.toGiveFansYour")}
          </div>
          <button type="button" className="balance-setlist-upload-btn" onClick={() => setUploadOpen(true)}>
            {tr("balancePage.uploadTrackHistory")}
          </button>
        </div>
      )}

      {/* Portaled to <body>: .balance-page is an isolated, clipped one-screen
          box, so a fixed sheet inside it would stack UNDER the tab bar. */}
      {cancelOpen &&
        createPortal(
          <div
            className="bal-modal-overlay"
            onClick={(e) => {
              if (e.target !== e.currentTarget || cancelBusy) return;
              setCancelOpen(false);
            }}
          >
            <div className="bal-modal" role="dialog" aria-modal="true" aria-label={tr("balancePage.cancelPlaylistSale")}>
              <h3>
                {tr("balancePage.cancelPlaylistSales", { count: setlistPending.count })}
              </h3>
              <p>
                {tr("balancePage.goesBackToFans", { count: setlistPending.count, amount: formatTxnAmount(setlistPending.total, setlistPending.currency || ownCurrency) })}
              </p>
              {cancelErr && <div className="bal-modal-err">{cancelErr}</div>}
              <div className="bal-modal-actions">
                <button type="button" className="bal-modal-keep" onClick={() => setCancelOpen(false)} disabled={cancelBusy}>
                  {tr("balancePage.keepIt")}
                </button>
                <button type="button" className="bal-modal-go" onClick={cancelSales} disabled={cancelBusy}>
                  {cancelBusy ? tr("balancePage.cancelling") : tr("balancePage.cancelSales", { count: setlistPending.count })}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Recent payouts → full history page. While a cash-out is on its way,
          the row and a green strip share one card (payout tracker, founder
          pick "Option 2", 2026-10-22); the strip opens the tracker sheet. */}
      {/* Refer & Earn money in the balance (1.5.2): its own line, above the payouts. */}
      <ReferralBonusRow byCurrency={referralByCur} />
      {moving ? (
        <div className={`ptk-card${setlistPending.count > 0 ? " ptk-card--with-sale" : ""}`}>
          {payoutsRow}
          <PayoutOnItsWay payout={moving} onOpen={() => setTrackerOpen(true)} />
        </div>
      ) : (
        payoutsRow
      )}
      {trackerOpen && moving && <PayoutTrackerSheet payout={moving} onClose={() => setTrackerOpen(false)} />}

      {/* Withdraw docked at the bottom — thumb zone */}
      <div className="balance-withdraw-dock">
        {/* Under the cashout minimum the button goes GREY (it used to stay bright
            green and only the Transfer modal said no — founder, build 39). It
            stays tappable: the tap explains why, in the talent's own currency. */}
        {withdrawTipOpen && cashoutLock && (
          <div className="balance-withdraw-tip" role="status">
            {/* Founder's words. True as written since the minimum is measured on the
                balance shown on the card (server lib/payouts/minCashout.js). */}
            <T k="balancePage.withdrawalsBeginAtTo" vars={{ amount: formatSingleAmount(cashoutLock.minimum, cashoutLock.currency, 0) }} tags={{ 0: (c) => <span className="balance-withdraw-tip-sub">{c}</span> }} />
          </div>
        )}
        <button
          type="button"
          className={`balance-withdraw-btn${withdrawLooksLocked ? " balance-withdraw-btn--locked" : ""}`}
          disabled={!hasBalance && takesMoney(gateLook)}
          aria-expanded={withdrawLocked ? withdrawTipOpen : undefined}
          onClick={async (e) => {
            if (withdrawLocked) {
              e.stopPropagation();
              // Free tier first, as always — on this visit's profile.
              if (gateProfile && !takesMoney(gateProfile)) return openMoneyGate(gateProfile);
              setWithdrawTipOpen((p) => !p);
              return;
            }
            // Free tier: Withdraw stays where it is and opens the money gate —
            // decided on this visit's profile (read now if the tap beat it).
            const gate = await freshGate();
            if (!gate) return;
            if (!takesMoney(gate)) return openMoneyGate(gate);
            // Same page, with the flow's own way in (hooks/useCashout opens on
            // { openCashout } once the funds are loaded, then clears it).
            navigate("/balance", { replace: true, state: { openCashout: true } });
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 3v10" /><path d="M8 9l4 4 4-4" /><path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
          </svg>
          {tr("balancePage.withdraw")}
        </button>
        {typeof payoutHoldDays === "number" && payoutHoldDays >= 7 && !holdLifted && (
          <p className="balance-hold-note">{tr("balancePage.firstPayoutsTakeLonger")}</p>
        )}
      </div>

      {/* Upload → review → deliver. Closing after a delivery refreshes the
          amber card (0 left → it disappears) and the balance number. */}
      {uploadOpen && (
        <SetlistUploadFlow
          pendingCount={setlistPending.count}
          pendingTotal={setlistPending.total}
          pendingCurrency={setlistPending.currency || ownCurrency}
          djName={displayName}
          onCancelSale={
            setlistPending.orderIds.length > 0
              ? () => {
                  setCancelErr(null);
                  setCancelOpen(true);
                }
              : undefined
          }
          onClose={(delivered) => {
            setUploadOpen(false);
            if (delivered) {
              fetchSetlistPending();
              bumpArmedRef.current = true;
              // Delivery captured the sales — the balance number just grew.
              setBalanceRefresh((k) => k + 1);
            }
          }}
        />
      )}

      {/* On the web, portaled to <body> like the cancel sheet (main #545):
          .balance-page is the page's own scroller, and iOS WebKit can clip a
          fixed sheet inside one (the Requests page keeps its copy outside its
          scroller too). The app renders it in place, as 1.5.2 (b49-b51) does. */}
      {cashout && (Capacitor.isNativePlatform() ? cashoutFlow : createPortal(cashoutFlow, document.body))}

    </div>
  );
};

export default BalancePage;
